import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { API_CONFIG } from '../config/api';
import { User, Mail, Phone, Building, MessageSquare, ClipboardSignature, Rocket, CheckCircle, X } from 'lucide-react';

const ProjectShareForm = ({ onClose = null }) => {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '+90 ',
    company_name: '',
    industry: '',
    project_title: '',
    project_description: '',
    budget_range: '',
    timeline: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const budgetRanges = [
    { value: '1000-5000', label: '₺1.000 - ₺5.000' },
    { value: '5000-15000', label: '₺5.000 - ₺15.000' },
    { value: '15000-50000', label: '₺15.000 - ₺50.000' },
    { value: '50000+', label: '₺50.000+' },
    { value: 'not-sure', label: 'Emin Değilim' }
  ];
  const timelines = [
    { value: '1-2weeks', label: '1-2 Hafta' },
    { value: '1month', label: '1 Ay' },
    { value: '2-3months', label: '2-3 Ay' },
    { value: '6months+', label: '6 Ay+' },
    { value: 'flexible', label: 'Esnek' }
  ];

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace('+90 ', '').replace(/\D/g, '');
    const limited = phoneNumber.slice(0, 10);
    
    let formatted = '+90 ';
    if (limited.length > 0) formatted += limited.slice(0, 3);
    if (limited.length > 3) formatted += ' ' + limited.slice(3, 6);
    if (limited.length > 6) formatted += ' ' + limited.slice(6, 8);
    if (limited.length > 8) formatted += ' ' + limited.slice(8, 10);
    
    return formatted;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setForm({...form, phone: formatted});
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      const payload = {
        service_id: 'project_share',
        service_title: `Proje Paylaşımı: ${form.project_title || 'Başlık yok'}`,
        customer_name: form.full_name,
        customer_email: form.email,
        customer_phone: form.phone || null,
        company_name: form.company_name || null,
        project_description: `${form.project_description}${form.industry ? `\n\nSektör: ${form.industry}` : ''}`,
        budget_range: form.budget_range || null,
        timeline: form.timeline || null,
        additional_requirements: null
      };
      const res = await fetch(`${API_CONFIG.BASE_URL}/backend/api.php?action=createQuoteRequest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Gönderim hatası');
      setSent(true);
      setTimeout(() => {
        if (onClose) onClose();
      }, 1200);
    } catch (err) {
      console.error(err);
      setError('Gönderim başarısız. Lütfen tekrar deneyin.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <Card className="w-full bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
        <CardContent className="p-10 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-green-800">Projeniz alındı!</h3>
            <p className="text-green-700">Uzmanlarımız kısa süre içinde sizinle iletişime geçecek.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full shadow-xl relative z-[60] border-0">
      <CardHeader className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-t-lg relative">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
            <Rocket className="w-6 h-6" />
          </div>
          <div>
            <CardTitle className="text-2xl">Projeni Paylaş</CardTitle>
            <p className="text-purple-100 mt-1">Detayları iletin, birlikte planlayalım</p>
          </div>
        </div>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white w-9 h-9 rounded-full"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={submit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="flex items-center gap-2"><User className="w-4 h-4"/> Ad Soyad *</Label>
              <Input id="full_name" value={form.full_name} onChange={e=>setForm({...form, full_name:e.target.value})} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2"><Mail className="w-4 h-4"/> E‑posta *</Label>
              <Input 
                id="email" 
                type="email" 
                placeholder="ornek@email.com"
                value={form.email} 
                onChange={e=>setForm({...form, email:e.target.value})} 
                pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                title="Geçerli bir e-posta adresi girin"
                required 
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2"><Phone className="w-4 h-4"/> Telefon</Label>
              <Input 
                id="phone" 
                type="tel"
                placeholder="+90 555 123 45 67"
                value={form.phone} 
                onChange={handlePhoneChange}
                title="Telefon numaranızı girin"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company_name" className="flex items-center gap-2"><Building className="w-4 h-4"/> Şirket</Label>
              <Input id="company_name" value={form.company_name} onChange={e=>setForm({...form, company_name:e.target.value})} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="industry">Sektör</Label>
              <Input id="industry" placeholder="Örn: E-ticaret, Sağlık, Eğitim" value={form.industry} onChange={e=>setForm({...form, industry:e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="project_title" className="flex items-center gap-2"><ClipboardSignature className="w-4 h-4"/> Proje Başlığı</Label>
              <Input id="project_title" placeholder="Kısa başlık" value={form.project_title} onChange={e=>setForm({...form, project_title:e.target.value})} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="budget_range">Bütçe Aralığı</Label>
              <select id="budget_range" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500" value={form.budget_range} onChange={e=>setForm({...form, budget_range:e.target.value})}>
                <option value="">Seçin</option>
                {budgetRanges.map(b => (<option key={b.value} value={b.value}>{b.label}</option>))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="timeline">Proje Süresi</Label>
              <select id="timeline" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500" value={form.timeline} onChange={e=>setForm({...form, timeline:e.target.value})}>
                <option value="">Seçin</option>
                {timelines.map(t => (<option key={t.value} value={t.value}>{t.label}</option>))}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="project_description" className="flex items-center gap-2"><MessageSquare className="w-4 h-4"/> Proje Detayları *</Label>
            <textarea id="project_description" rows={5} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500" value={form.project_description} onChange={e=>setForm({...form, project_description:e.target.value})} required placeholder="Ne yapmak istiyorsunuz? Hedefleriniz, kapsam ve teknolojiler..." />
          </div>
          {error && (
            <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">
              {error}
            </div>
          )}
          <div className="flex items-center justify-end pt-2">
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={onClose}>Vazgeç</Button>
              <Button type="submit" disabled={sending} className={`${sending? 'bg-purple-300':'bg-purple-600 hover:bg-purple-700'} text-white relative z-[70]`}>
                {sending ? 'Gönderiliyor...' : 'Gönder'}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ProjectShareForm;


